/* eslint-disable spaced-comment */
import {
  additionalServicesFromInitial,
  additionalServicesInvalid,
  prepareAdditionalServices,
  type AdditionalServiceInput,
  type AdditionalServicePayload
} from './additionalServices'
import { isBlankString, isMissingId, oneOf } from './sellContractFormChecks'

export type { AdditionalServiceInput, AdditionalServicePayload, ServicePriceType } from './additionalServices'
export type MileageStatement = 'known' | 'unknown' | 'custom'
export type PaymentDueType = 'handover' | 'custom'
export type RegistrationStateCode = '' | 'deregistered' | 'registered'
export type RegistrationByCode = '' | 'seller' | 'buyer'
export type ClientParty = '' | 'personal' | 'company'

export interface SellContractFormData {
  client_id: number
  dealer_id: number
  motorbike_id: number
  motorbike_name?: string
  motorbike_mileage?: number | null
  motorbike_year?: string
  client_name?: string
  client_email?: string
  client_phone?: string
  client_address?: string
  client_type: ClientParty
  business_purpose: string
  previous_owners_basis: string
  delivery_date: string
  net_price: number
  down_payment: string
  down_payment_method: string
  down_payment_type: string
  down_payment_date: string
  vin: string
  vat_option: string
  price?: string
  price_type?: 'net' | 'gross'
  delivery_time_span: string
  contract_binding_type: string
  delivery_type: string
  delivery_address: string
  payment_method: string
  deviations: string
  deviations_description: string
  previous_owner_deviations: string
  previous_owner_deviations_description: string
  shorten_warranty: boolean
  exclude_update_obligations: boolean
  delivery_period: string
  language: string
  currency: string
  mileage_statement: MileageStatement
  mileage_statement_text: string
  contract_note: string
  has_objective_deviations: boolean
  objective_deviation_1: string
  payment_due_type: PaymentDueType
  payment_due_text: string
  registration_state: RegistrationStateCode
  registration_by: RegistrationByCode
  key_count: string
  other_equipment: string
  missing_items: string
  additional_services: AdditionalServiceInput[]
}

export type SellContractRequestData = Omit<SellContractFormData, 'additional_services'> & {
  additional_services: AdditionalServicePayload[]
}

export const sellContractFormDefaults = (): SellContractFormData => ({
  client_id: 0,
  client_type: '',
  business_purpose: '',
  previous_owners_basis: '',
  dealer_id: 0,
  motorbike_id: 0,
  delivery_date: '',
  net_price: 0,
  down_payment: '0',
  down_payment_method: '',
  down_payment_type: '',
  down_payment_date: '',
  vin: '',
  vat_option: '',
  price: '',
  price_type: 'gross',
  delivery_time_span: 'exact_date',
  contract_binding_type: 'binding',
  delivery_type: 'client',
  delivery_address: '',
  payment_method: '',
  deviations: 'deviations_not_specified',
  deviations_description: '',
  previous_owner_deviations: 'previous_owner_not_specified',
  previous_owner_deviations_description: '',
  shorten_warranty: true,
  exclude_update_obligations: true,
  delivery_period: '',
  language: 'de',
  currency: 'EUR',
  mileage_statement: 'known',
  mileage_statement_text: '',
  contract_note: '',
  has_objective_deviations: false,
  objective_deviation_1: '',
  payment_due_type: 'handover',
  payment_due_text: '',
  registration_state: '',
  registration_by: '',
  key_count: '',
  other_equipment: '',
  missing_items: '',
  additional_services: []
})

export const sellContractEditValues = (initial: Partial<SellContractFormData>): SellContractFormData => {
  const objectiveDeviation = initial.objective_deviation_1 ?? ''
  return {
    ...sellContractFormDefaults(),
    ...initial,
    down_payment: String(initial.down_payment ?? '0'),
    price: String(initial.price ?? initial.net_price ?? ''),
    price_type: initial.price_type ?? 'net',
    currency: initial.currency === 'PLN' ? 'PLN' : 'EUR',
    mileage_statement: oneOf(initial.mileage_statement, ['known', 'unknown', 'custom'] as const, 'known'),
    mileage_statement_text: initial.mileage_statement_text ?? '',
    contract_note: initial.contract_note ?? '',
    client_type: oneOf(initial.client_type, ['', 'personal', 'company'] as const, ''),
    business_purpose: initial.business_purpose ?? '',
    previous_owners_basis: initial.previous_owners_basis ?? '',
    has_objective_deviations:
      initial.has_objective_deviations === true ||
      (initial.has_objective_deviations !== false && objectiveDeviation.trim().length > 0),
    objective_deviation_1: objectiveDeviation,
    payment_due_type: oneOf(initial.payment_due_type, ['handover', 'custom'] as const, 'handover'),
    payment_due_text: initial.payment_due_text ?? '',
    registration_state: oneOf(initial.registration_state, ['', 'deregistered', 'registered'] as const, ''),
    registration_by: oneOf(initial.registration_by, ['', 'seller', 'buyer'] as const, ''),
    delivery_address: initial.delivery_address ?? '',
    key_count: initial.key_count ?? '',
    other_equipment: initial.other_equipment ?? '',
    missing_items: initial.missing_items ?? '',
    additional_services: additionalServicesFromInitial(initial.additional_services)
  }
}

const dependentKeys = ['payment_due_text', 'mileage_statement_text', 'objective_deviation_1'] as const

export const clearDependentSellContractFields = (values: SellContractFormData): SellContractFormData => {
  const next = { ...values }
  if (next.payment_due_type !== 'custom') {
    next.payment_due_text = ''
  }
  if (next.mileage_statement !== 'custom') {
    next.mileage_statement_text = ''
  }
  if (!next.has_objective_deviations) {
    next.objective_deviation_1 = ''
  }
  return next
}

export const dependentSellContractPatch = (
  values: SellContractFormData,
  patch: Partial<SellContractFormData>
): Partial<SellContractFormData> => {
  const next = clearDependentSellContractFields({ ...values, ...patch })
  const changes: Partial<SellContractFormData> = {}
  dependentKeys.forEach((key) => {
    if (next[key] !== values[key]) {
      changes[key] = next[key]
    }
  })
  return changes
}

export const sellContractRequestValues = (values: SellContractFormData): SellContractRequestData => {
  const cleared = clearDependentSellContractFields(values)
  const { previous_owner_deviations_description, ...request } = cleared
  void previous_owner_deviations_description
  return {
    ...request,
    previous_owner_deviations_description: '',
    additional_services: prepareAdditionalServices(cleared.additional_services ?? [])
  }
}

export const validateSellContractForm = (
  values: SellContractFormData
): Partial<Record<keyof SellContractFormData, string>> => {
  const errors: Partial<Record<keyof SellContractFormData, string>> = {}

  if (isMissingId(values.client_id)) {
    errors.client_id = 'required'
  }
  if (isMissingId(values.motorbike_id)) {
    errors.motorbike_id = 'required'
  }
  if (typeof values.net_price !== 'number' || Number.isNaN(values.net_price) || values.net_price === 0) {
    errors.net_price = 'required'
  }

  const requiredStrings = [
    'down_payment',
    'vin',
    'vat_option',
    'delivery_time_span',
    'contract_binding_type',
    'delivery_type',
    'price',
    'language',
    'currency',
    'delivery_date'
  ] as const

  requiredStrings.forEach((field) => {
    if (isBlankString(values[field])) {
      errors[field] = 'required'
    }
  })

  if (additionalServicesInvalid(values.additional_services ?? [])) {
    errors.additional_services = 'required'
  }

  const conditionalRequirements: Array<{
    when: (current: SellContractFormData) => boolean
    fields: Array<keyof SellContractFormData>
  }> = [
    {
      when: (current) => {
        const amount = Number(current.down_payment)
        return !Number.isNaN(amount) && amount !== 0
      },
      fields: ['down_payment_method', 'down_payment_type']
    },
    {
      when: (current) => {
        const amount = Number(current.down_payment)
        return !Number.isNaN(amount) && amount !== 0 && current.down_payment_type === 'date'
      },
      fields: ['down_payment_date']
    },
    { when: (current) => current.delivery_time_span === 'time_span', fields: ['delivery_period'] },
    { when: (current) => current.mileage_statement === 'custom', fields: ['mileage_statement_text'] },
    {
      when: (current) => current.client_type !== 'company' && current.has_objective_deviations,
      fields: ['objective_deviation_1']
    },
    {
      when: () => true,
      fields: ['payment_due_type']
    },
    {
      when: (current) => current.client_type !== 'company',
      fields: ['registration_state', 'registration_by']
    },
    { when: (current) => current.payment_due_type === 'custom', fields: ['payment_due_text'] }
  ]

  conditionalRequirements.forEach((rule) => {
    if (!rule.when(values)) {
      return
    }
    rule.fields.forEach((field) => {
      const value = values[field]
      if (typeof value === 'string' && isBlankString(value)) {
        errors[field] = 'required'
      }
    })
  })

  return errors
}
